/**
 * Web应用特定的翻译上下文提供者
 *
 * 扩展核心翻译功能，提供Web应用所需的特定功能并集成新的缓存/历史机制
 */

import React, { useCallback, createContext, useContext, useMemo } from 'react';
import {
  TranslationProvider as CoreTranslationProvider,
  useTranslation as useCoreTranslation,
  useModelState,
  useDocument,
  useLanguageState,
  TranslationContextType,
} from '@readsavor/core';
import { WebTranslationContextType } from '../hooks/useWebTranslation.js';
import { AppError, SelectionData } from '@readsavor/types';
import { useWebActiveDocTranslationContext } from './WebTranslationHistoryProvider.js';
import { useWebTranslationCache } from '../hooks/useWebTranslationCache.js';

/**
 * Web翻译提供者属性接口
 */
interface WebTranslationProviderProps {
  /**
   * 子组件
   */
  children: React.ReactNode;

  /**
   * 文档就绪回调
   */
  onDocumentReady?: (result: { isFromHistory: boolean; docId?: number }) => void;

  /**
   * 翻译错误回调
   */
  onTranslationError?: (error: AppError) => void;

  /**
   * 初始源语言
   */
  initialSourceLanguage?: string;

  /**
   * 初始目标语言
   */
  initialTargetLanguage?: string;

  /**
   * 初始模型
   */
  initialModel?: string;
}

/**
 * Web翻译上下文
 * 默认值设置为undefined，必须通过Provider提供
 */
export const WebTranslationContext = createContext<WebTranslationContextType | undefined>(
  undefined
);

/**
 * 使用Web翻译上下文钩子
 * 提供Web翻译上下文的便捷访问
 *
 * @returns Web翻译上下文值
 * @throws 如果在WebTranslationProvider之外使用，会抛出错误
 */
export const useWebTranslationContext = (): WebTranslationContextType => {
  const context = useContext(WebTranslationContext);

  if (context === undefined) {
    throw new Error('useWebTranslationContext must be used within a WebTranslationProvider');
  }

  return context;
};

/**
 * Web翻译提供者组件
 * 整合核心翻译功能和Web应用特定功能
 */
export const WebTranslationProvider: React.FC<WebTranslationProviderProps> = ({ children }) => {
  const coreTranslation: TranslationContextType = useCoreTranslation();
  const webActiveDocCtx = useWebActiveDocTranslationContext();
  const {
    isSelectionCached: webIsSelectionCached,
    checkAndUpdateCacheStatus,
    fetchAndPrepareCachedItem,
  } = useWebTranslationCache();

  const { model, setModel } = useModelState();
  useDocument();
  const languageHookValue = useLanguageState();

  const viewCachedTranslationHandler = useCallback(
    async (selection: SelectionData) => {
      const preparedCacheItem = await fetchAndPrepareCachedItem(selection);

      if (preparedCacheItem && coreTranslation.setDisplayedTranslation) {
        coreTranslation.setDisplayedTranslation({
          originalText: preparedCacheItem.originalText,
          translatedText: preparedCacheItem.translatedText,
          analysis: preparedCacheItem.analysis || undefined,
        });
      } else if (preparedCacheItem) {
        console.warn(
          '[WebTranslationProvider] coreTranslation.setDisplayedTranslation is not available. Cannot display cached item.'
        );
        coreTranslation.setSelectedText(selection);
      }
    },
    [fetchAndPrepareCachedItem, coreTranslation]
  );

  const contextValue: WebTranslationContextType = useMemo(() => {
    // Removed noisy debug log: WebTranslationProvider: Creating context value

    return {
      ...coreTranslation,
      model,
      setModel,
      isSelectionCached: webIsSelectionCached,
      viewCachedTranslation: viewCachedTranslationHandler,
      checkAndUpdateCacheStatus: checkAndUpdateCacheStatus,
      saveTranslationResult: webActiveDocCtx.saveNewTranslationForCurrentDoc,
      activeIdentity: webActiveDocCtx.activeIdentity,
      sourceLanguage: languageHookValue.sourceLanguage,
      targetLanguage: languageHookValue.targetLanguage,
      setSourceLanguage: languageHookValue.setSourceLanguage,
      setTargetLanguage: languageHookValue.setTargetLanguage,
      isValidPair: languageHookValue.isValidPair,
      sourceLanguages: languageHookValue.sourceLanguages,
      targetLanguages: languageHookValue.targetLanguages,
      setCurrentDocument: languageHookValue.setCurrentDocument,
      getCurrentDocumentIdentity: languageHookValue.getCurrentDocumentIdentity,
    };
  }, [
    coreTranslation,
    model,
    setModel,
    webIsSelectionCached,
    viewCachedTranslationHandler,
    checkAndUpdateCacheStatus,
    webActiveDocCtx.saveNewTranslationForCurrentDoc,
    webActiveDocCtx.activeIdentity,
    languageHookValue,
  ]);

  return (
    <WebTranslationContext.Provider value={contextValue}>{children}</WebTranslationContext.Provider>
  );
};

/**
 * 扩展的翻译提供者组件
 * 组合核心翻译提供者和Web特定翻译提供者
 */
export const ExtendedTranslationProvider: React.FC<WebTranslationProviderProps> = props => {
  return (
    <CoreTranslationProvider
      initialSourceLanguage={props.initialSourceLanguage}
      initialTargetLanguage={props.initialTargetLanguage}
      initialModel={props.initialModel}
    >
      <WebTranslationProvider {...props} />
    </CoreTranslationProvider>
  );
};
